/**
 * Maps API and network failures to copy users can act on, or directs them to support.
 * Technical `error` strings from the API are kept in logs; UI should use these helpers.
 */

export const SUPPORT_EMAIL = 'support@provault.io';

const SUPPORT_LINE = ` If you need help, contact us at ${SUPPORT_EMAIL}.`;

/** Exact API `error` strings (lowercase) → user-facing copy. */
const API_ERROR_MAP = {
  'invalid csrf token':
    'Your session was interrupted. Refresh the page, then try again. If you were signed out, sign in and retry.',
  unauthorized:
    'Your session has expired or you are not signed in. Please sign in and try again.',
  forbidden:
    'You do not have permission to do that with your current account. Sign in with the correct account or contact support.',
  'bad request':
    'We could not process that request. Check your entries and try again.',
  'unsupported media type':
    'That file type is not supported. Use a PDF, image, or DOCX file and try again.',
  'invalid invite':
    'This invite link is not valid. Open the link from your invitation email or ask the person who invited you to send a new invite.',
  'invite already used':
    'This invite has already been used. Sign in with the account you created, or ask for a new invitation.',
  'invite expired':
    'This invite has expired. Ask the person who invited you to send a new invitation.',
  'invite email missing':
    'This invite is incomplete. Ask the person who invited you to send a new invitation.',
  'missing invite token':
    'This invite link is incomplete. Open the full link from your invitation email.',
  'invalid id_token':
    'Sign-in could not be completed. Please start again from the login screen.',
  'missing tokens in response':
    'Sign-in could not be completed. Please start again from the login screen.',
  'token exchange request failed':
    'Sign-in could not be completed. Please start again from the login screen.',
  'owner context not permitted':
    'That action is only available in your owner account. Switch to your owner profile and try again.',
  'nominee context not permitted':
    'That action is only available in your nominee account. Switch to your nominee profile and try again.',
  'active_owner_sub is only valid for nominee context':
    'Please switch to your nominee profile before continuing.',
  'you do not have nominee access to this owner':
    'You do not have access to this estate. Check that you accepted the correct invitation.',
  'access not granted by owner':
    'The account owner has not shared this section with you yet. Ask them to turn on the relevant sharing options under Nominees in their ProVault account.',
  'only owners can mutate assets':
    'Only the account owner can add or change assets. Sign in as the owner to continue.',
  'only owners can create assets':
    'Only the account owner can add assets. Sign in as the owner to continue.',
  'only owners can update assets':
    'Only the account owner can update assets. Sign in as the owner to continue.',
  'only owners can delete assets':
    'Only the account owner can remove assets. Sign in as the owner to continue.',
  'only owners can generate wills':
    'Only the account owner can generate a will. Sign in as the owner to continue.',
  'only owners can access will data':
    'Only the account owner can view or manage will documents here.',
  'only owners can upload wills':
    'Only the account owner can upload a signed will.',
  'only owners can generate lpa/poa templates':
    'Only the account owner can generate LPA/PoA documents.',
  'only owners can access lpa/poa data':
    'Only the account owner can view or manage LPA/PoA documents here.',
  'only owners can upload lpa/poa files':
    'Only the account owner can upload signed LPA/PoA files.',
  'only owners can view attachments':
    'Only the account owner can view these attachments.',
  'only owners can upload attachments':
    'Only the account owner can upload attachments.',
  'only owners can finalize uploads':
    'Only the account owner can complete this upload.',
  'storage quota exceeded (10gb limit)':
    'You have reached your storage limit (10 GB). Remove old files or contact support to discuss options.',
  'asset not found':
    'That item could not be found. It may have been removed—refresh the page and try again.',
  'not found':
    'We could not find what you asked for. Refresh the page and try again.',
  'no current will':
    'There is no will on file yet. Generate or upload a will first.',
  'no current lpa/poa file':
    'There is no LPA/PoA file on file yet. Generate or upload one first.',
  'invalid file name':
    'That file name is not valid. Rename the file and try again.',
  'no fields to update':
    'Enter at least one change before saving.',
  'attachments are only supported for other asset categories':
    'Attachments can only be added to items under Other assets.',
  'missing asset id':
    'That asset could not be identified. Refresh the page and try again.',
  'finalize failed':
    'The upload could not be completed. Try uploading the file again.',
  'security error':
    'We could not read that data securely.' + SUPPORT_LINE,
  'generation failed':
    'We could not generate that document.' + SUPPORT_LINE,
};

/** Substrings in API errors that indicate a server-side problem → append support guidance. */
const SUPPORT_HINT_PATTERNS = [
  /not configured/i,
  /^failed to /i,
  /cognito/i,
  /decrypt/i,
  /token exchange/i,
  /establish session/i,
  /failed to resolve/i,
  /failed to load/i,
  /failed to validate/i,
  /failed to create/i,
  /failed to update/i,
  /failed to list/i,
  /failed to inspect/i,
  /failed to attach/i,
  /failed to switch/i,
  /failed to finalize/i,
  /failed to initialize/i,
  /generation failed/i,
  /security error/i,
];

function normalizeKey(value) {
  return String(value || '').trim().toLowerCase();
}

function extractApiPayload(err) {
  const data = err?.response?.data;
  if (!data) return { status: err?.response?.status, code: '', error: '', message: '' };
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data);
      return {
        status: err?.response?.status,
        code: parsed?.code || '',
        error: parsed?.error || '',
        message: parsed?.message || '',
      };
    } catch {
      return { status: err?.response?.status, code: '', error: data, message: '' };
    }
  }
  return {
    status: err?.response?.status,
    code: data?.code || '',
    error: typeof data?.error === 'string' ? data.error : '',
    message: typeof data?.message === 'string' ? data.message : '',
  };
}

function isAlreadyUserFriendly(text) {
  if (!text || typeof text !== 'string') return false;
  const t = text.trim();
  if (t.length < 12 || t.length > 500) return false;
  if (API_ERROR_MAP[normalizeKey(t)]) return false;
  if (SUPPORT_HINT_PATTERNS.some((p) => p.test(t))) return false;
  if (/^invalid csrf token$/i.test(t)) return false;
  if (/^unauthorized$/i.test(t)) return false;
  if (/^forbidden$/i.test(t)) return false;
  if (/^bad request$/i.test(t)) return false;
  if (/contact support/i.test(t)) return true;
  if (/\bplease\b/i.test(t)) return true;
  if (/sign in/i.test(t)) return true;
  if (t.includes('.') && t.split(/\s+/).length >= 6) return true;
  return false;
}

function withSupportIfNeeded(message, status) {
  if (!message) return message;
  if (/contact us at/i.test(message) || /contact support/i.test(message)) return message;
  if (status >= 500 || SUPPORT_HINT_PATTERNS.some((p) => p.test(message))) {
    return message.endsWith('.') ? `${message}${SUPPORT_LINE}` : `${message}.${SUPPORT_LINE}`;
  }
  return message;
}

/**
 * @param {unknown} err - Axios error or similar
 * @param {{ fallback?: string, includeSupportForUnknown?: boolean }} [options]
 * @returns {string}
 */
export function getUserFacingErrorMessage(err, options = {}) {
  const {
    fallback = 'Something went wrong. Please try again.',
    includeSupportForUnknown = true,
  } = options;

  if (err?.userFacingMessage) return err.userFacingMessage;

  if (!err?.response) {
    const code = err?.code || '';
    if (code === 'ERR_NETWORK' || /network error/i.test(String(err?.message || ''))) {
      return 'We could not reach ProVault. Check your internet connection and try again.';
    }
    if (code === 'ECONNABORTED') {
      return 'The request took too long. Please try again.';
    }
    return includeSupportForUnknown ? `${fallback}${SUPPORT_LINE}` : fallback;
  }

  const { status, code, error: apiError, message: apiMessage } = extractApiPayload(err);

  if (code === 'NOMINEE_REQUIRED' && apiMessage) {
    return apiMessage;
  }

  if (apiMessage && isAlreadyUserFriendly(apiMessage)) {
    return apiMessage;
  }

  const mapped = API_ERROR_MAP[normalizeKey(apiError)];
  if (mapped) return mapped;

  if (apiError && isAlreadyUserFriendly(apiError)) {
    return apiError;
  }

  if (status === 401) {
    return API_ERROR_MAP.unauthorized;
  }
  if (status === 403 && normalizeKey(apiError) !== 'invalid csrf token') {
    if (apiError && isAlreadyUserFriendly(apiError)) return apiError;
    return API_ERROR_MAP.forbidden;
  }
  if (status === 404) {
    return API_ERROR_MAP['not found'];
  }
  if (status === 409 && apiMessage) {
    return apiMessage;
  }
  if (status === 413) {
    return API_ERROR_MAP['storage quota exceeded (10gb limit)'];
  }
  if (status >= 500) {
    const base =
      status === 502
        ? 'ProVault is temporarily unavailable.'
        : 'Something went wrong on our side.';
    return `${base}${SUPPORT_LINE}`;
  }

  if (apiMessage) return withSupportIfNeeded(apiMessage, status);
  if (apiError) return withSupportIfNeeded(apiError, status);

  return includeSupportForUnknown ? `${fallback}${SUPPORT_LINE}` : fallback;
}

/**
 * Attach `userFacingMessage` for axios errors (used by the API client interceptor).
 * @param {import('axios').AxiosError} err
 */
export function attachUserFacingMessage(err) {
  if (!err || err.userFacingMessage) return err;
  try {
    err.userFacingMessage = getUserFacingErrorMessage(err);
  } catch (_) {}
  return err;
}
